import { CalendarClock, FileBarChart, Eye, Download } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import { withJsonErrorBody } from '../../lib/blob-error-body'
import { ReportRecipientsSection } from '../../components/report-recipients/ReportRecipientsSection'
import { TimezoneSelect } from '../../components/CompanyTimezone'
import { ALL_TIMEZONES } from '../../lib/timezones'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'

/** Master Admin → Usage Report. The platform (consolidated) digest is driven by
 *  THREE independent schedulers — Daily, Weekly and Monthly — each with its own
 *  on/off + send time, so any combination can send as its own email with that
 *  period's data. Recipients are structured records (Name / Email ID /
 *  Designation / Status) stored on the platform `usage_report` config. */

// The three platform schedulers, in display order.
const SCHEDULE_ROWS = [
  {
    cadence: 'daily',
    label: 'Daily',
    title: 'Daily',
    noun: 'day',
    gap: '20 hours',
    hint: 'Sent every day — summarises the previous full day',
    // With the new report selected (engine 'v2').
    hintV2:
      'Sent every day — the Daily Platform Usage Report for the previous full UTC day, with the Excel workbook attached',
  },
  {
    cadence: 'weekly',
    label: 'Weekly (Monday)',
    title: 'Weekly',
    noun: 'week',
    gap: '6 days',
    hint: 'Sent every Monday — summarises the previous week',
    hintV2:
      'Sent every Monday — the Weekly Platform Usage Report for the previous full UTC week (Monday–Sunday), with the Excel workbook attached',
  },
  {
    cadence: 'monthly',
    label: 'Monthly (1st of the month)',
    title: 'Monthly',
    noun: 'month',
    gap: '27 days',
    hint: 'Sent on the 1st — summarises the previous month',
    hintV2:
      'Sent on the 1st — the Monthly Platform Usage Report for the previous full UTC calendar month, with the Excel workbook attached',
  },
]

const CONFIG_KEY = ['platform-usage-report', 'config']

const fieldClass =
  'rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] disabled:opacity-60'

/** On/off switch — same shape as the IP Access / Maintenance settings, so this
 *  surface feels consistent with the rest of the Super Admin area. */
function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ' +
        (checked
          ? 'bg-[color:var(--color-primary)]'
          : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

/** One scheduler row: enable toggle + label + its own send time. The time input
 *  keeps a local draft so we save once (on blur), not on every spinner tick;
 *  re-syncs to the persisted value using React's "adjust state during render"
 *  pattern (no setState-in-effect). */
function ScheduleRow({ cadence, label, hint, schedule, onSave, busy, children }) {
  const enabled = schedule?.enabled ?? false
  const sendAt = schedule?.sendAt ?? '09:00'

  const [timeDraft, setTimeDraft] = useState(sendAt)
  const [lastSynced, setLastSynced] = useState(sendAt)
  if (sendAt !== lastSynced) {
    setLastSynced(sendAt)
    setTimeDraft(sendAt)
  }

  return (
    <div className="py-3.5">
      <div className="flex flex-wrap items-center gap-4">
        <Toggle
          checked={enabled}
          disabled={busy}
          label={`Enable the ${label} report`}
          onChange={(v) => onSave(cadence, { enabled: v })}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0">
            {label}
          </p>
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">{hint}</p>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`ur-time-${cadence}`}
            className="text-xs text-[color:var(--color-ink-muted)]"
          >
            Send time
          </label>
          <input
            id={`ur-time-${cadence}`}
            type="time"
            className={fieldClass}
            value={timeDraft}
            disabled={busy || !enabled}
            onChange={(e) => setTimeDraft(e.target.value)}
            onBlur={() => {
              if (timeDraft && timeDraft !== sendAt) {
                onSave(cadence, { sendAt: timeDraft })
              }
            }}
          />
        </div>
      </div>
      {children}
    </div>
  )
}

/** The two versions a schedule can send (per cadence wording). */
const engineOptions = (row) => [
  { value: 'legacy', label: `Current ${row.noun === 'day' ? 'daily' : row.title.toLowerCase()} digest` },
  { value: 'v2', label: `${row.title} Platform Usage Report (new)` },
]

/** Settings under each schedule row: which report is sent, and the time zone
 *  its send time is read in (empty → the schedule timezone above). */
function ReportOptions({ row, schedule, tzOptions, templateReady, onSave, busy }) {
  const engine = schedule?.engine ?? 'legacy'
  const timezone = schedule?.timezone ?? ''
  const c = row.cadence
  return (
    <div className="mt-3 ml-0 grid gap-3 sm:ml-[52px] sm:grid-cols-2">
      <div className="min-w-0">
        <label
          htmlFor={`ur-${c}-engine`}
          className="text-xs font-medium text-[color:var(--color-ink-muted)]"
        >
          Report version
        </label>
        <select
          id={`ur-${c}-engine`}
          className={`${fieldClass} mt-1 w-full`}
          value={engine}
          disabled={busy}
          onChange={(e) => onSave(c, { engine: e.target.value })}
        >
          {engineOptions(row).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-0">
        <label
          htmlFor={`ur-${c}-timezone`}
          className="text-xs font-medium text-[color:var(--color-ink-muted)]"
        >
          {row.title} time zone
        </label>
        <div className="mt-1">
          <TimezoneSelect
            id={`ur-${c}-timezone`}
            ariaLabel={`${row.title} time zone`}
            disabled={busy}
            value={timezone}
            onChange={(tz) => onSave(c, { timezone: tz || '' })}
            options={tzOptions}
            emptyLabel="Not set — use the schedule timezone"
          />
        </div>
      </div>
      <p className="m-0 text-xs text-[color:var(--color-ink-subtle)] sm:col-span-2">
        The {row.title.toLowerCase()} send time is read in this time zone. A
        change of version takes effect from the next send. If the other version
        has sent in the last {row.gap}, the new one waits for its next send
        time, so you never get both for the same {row.noun}.
      </p>
      {engine === 'v2' && templateReady === false ? (
        <p
          role="alert"
          className="m-0 rounded-lg border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] px-3 py-2 text-xs text-[color:var(--color-danger-text)] sm:col-span-2"
        >
          The new report can&rsquo;t be sent yet: its email template isn&rsquo;t
          set up. Assign a template to &ldquo;Usage Report (Platform,{' '}
          {row.title})&rdquo; on Email Templates → Assignments.
        </p>
      ) : null}
    </div>
  )
}

function DeliveryScheduleSection({ schedules, timezone, loading, templatesReady }) {
  const queryClient = useQueryClient()
  const save = useMutation({
    mutationFn: (patch) => api.put('/super-admin/platform/usage-report', patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      toast.success('Usage Report schedule updated.')
    },
    onError: (e) => toast.error(extractApiError(e).message),
  })

  const busy = loading || save.isPending
  const onSave = (cadence, patch) =>
    save.mutate({ platformSchedules: { [cadence]: patch } })
  // Empty string clears the override on the server (→ its default timezone).
  const onSaveTimezone = (tz) => save.mutate({ timezone: tz || '' })

  // Prepend the saved zone if it isn't in the standard catalogue, so it always
  // renders as the selected option (mirrors the Company timezone picker).
  const tzOptions = useMemo(() => {
    if (timezone && !ALL_TIMEZONES.some((o) => o.value === timezone)) {
      return [{ value: timezone, name: timezone, offset: '' }, ...ALL_TIMEZONES]
    }
    return ALL_TIMEZONES
  }, [timezone])
  // Same for each schedule's own time zone.
  const ownZones = SCHEDULE_ROWS.map((r) => schedules?.[r.cadence]?.timezone ?? '').join('|')
  const tzOptionsByCadence = useMemo(() => {
    const zones = ownZones.split('|')
    return Object.fromEntries(
      SCHEDULE_ROWS.map((r, i) => {
        const tz = zones[i]
        const list = tz && !ALL_TIMEZONES.some((o) => o.value === tz)
          ? [{ value: tz, name: tz, offset: '' }, ...ALL_TIMEZONES]
          : ALL_TIMEZONES
        return [r.cadence, list]
      }),
    )
  }, [ownZones])

  return (
    <section className="mb-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 flex items-center gap-2">
        <CalendarClock size={18} className="text-[color:var(--color-accent-strong)]" />
        Delivery schedule
      </h2>
      <p className="mt-1.5 text-sm text-[color:var(--color-ink-muted)]">
        Turn on any combination of the schedules below — each sends as its own
        email with that period&rsquo;s figures. This applies to the Master Admin
        digest only; each company keeps its own weekly / monthly report at 09:00.
      </p>

      <div className="mt-4 max-w-md">
        <label
          htmlFor="ur-timezone"
          className="text-xs font-medium text-[color:var(--color-ink-muted)]"
        >
          Schedule timezone
        </label>
        <div className="mt-1">
          <TimezoneSelect
            id="ur-timezone"
            ariaLabel="Schedule timezone"
            value={timezone ?? ''}
            onChange={onSaveTimezone}
            options={tzOptions}
          />
        </div>
        <p className="mt-1.5 text-xs text-[color:var(--color-ink-subtle)]">
          Send times and the weekly / monthly anchor days are evaluated in this
          timezone (24-hour) unless a schedule has its own time zone below.
          Leave unset to use the platform default.
        </p>
      </div>

      <div className="mt-3 divide-y divide-[color:var(--color-rule)] border-y border-[color:var(--color-rule)]">
        {SCHEDULE_ROWS.map((row) => {
          const schedule = schedules?.[row.cadence]
          return (
            <ScheduleRow
              key={row.cadence}
              cadence={row.cadence}
              label={row.label}
              hint={schedule?.engine === 'v2' ? row.hintV2 : row.hint}
              schedule={schedule}
              onSave={onSave}
              busy={busy}
            >
              <ReportOptions
                row={row}
                schedule={schedule}
                tzOptions={tzOptionsByCadence[row.cadence]}
                templateReady={templatesReady?.[row.cadence]}
                onSave={onSave}
                busy={busy}
              />
            </ScheduleRow>
          )
        })}
      </div>
    </section>
  )
}

const PREVIEW_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

/** Render-only PREVIEW of the platform digest — opens the email exactly as it
 *  would be sent (in a sandboxed iframe), its subject and its "Goes to" list,
 *  plus an Excel download. Sends nothing and records nothing (the endpoint is
 *  render-only). The HTML is sandboxed (`sandbox=""`: no scripts, no same-origin)
 *  so an emailed value can never execute in the admin's session. */
function PreviewDialog({ open, onClose, engines }) {
  const [frequency, setFrequency] = useState('weekly')
  // Which version to preview per frequency; unset → the one that schedule sends.
  const [versions, setVersions] = useState({})
  const [downloading, setDownloading] = useState(false)
  const engine = versions[frequency] ?? engines?.[frequency] ?? 'legacy'
  const params = { frequency, engine }

  const previewQuery = useQuery({
    queryKey: ['platform-usage-report', 'preview', frequency, engine],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/usage-report/preview', {
        params,
      })
      return res.data
    },
    enabled: open,
  })
  const data = previewQuery.data

  async function downloadExcel() {
    setDownloading(true)
    try {
      const res = await api.get(
        '/super-admin/platform/usage-report/preview.xlsx',
        { params, responseType: 'blob' },
      )
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download =
        engine === 'v2'
          ? `taskmagix-${frequency}-platform-usage-report.xlsx`
          : `taskmagix-usage-report-${frequency}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast.error(extractApiError(await withJsonErrorBody(e)).message)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Preview report"
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={downloadExcel}
            loading={downloading}
            disabled={previewQuery.isLoading || !data?.html}
          >
            <Download size={16} /> Download Excel
          </Button>
        </>
      }
    >
      {/* Frequency selector — which cadence's email to preview. */}
      <div
        role="tablist"
        aria-label="Preview frequency"
        className="inline-flex items-center rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-0.5"
      >
        {PREVIEW_FREQUENCIES.map((f) => {
          const selected = f.value === frequency
          return (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setFrequency(f.value)}
              className={
                'px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ' +
                (selected
                  ? 'bg-[color:var(--color-primary)] text-white shadow-sm'
                  : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
              }
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {/* The current digest or the new report, for the chosen frequency. */}
      <div
        role="tablist"
        aria-label="Report version"
        className="ml-2 inline-flex items-center rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-0.5"
      >
        {[{ value: 'legacy' }, { value: 'v2' }].map((o) => {
          const selected = o.value === engine
          return (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setVersions((v) => ({ ...v, [frequency]: o.value }))}
              className={
                'px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ' +
                (selected
                  ? 'bg-[color:var(--color-primary)] text-white shadow-sm'
                  : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
              }
            >
              {o.value === 'legacy' ? 'Current digest' : 'New report'}
            </button>
          )
        })}
      </div>

      {/* Goes-to + subject. */}
      <div className="mt-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-xs">
        {data ? (
          data.goesTo?.length ? (
            <p className="m-0 text-[color:var(--color-ink-muted)]">
              <span className="font-medium text-[color:var(--color-ink-strong)]">
                Goes to:{' '}
              </span>
              {data.goesTo.join(', ')}
            </p>
          ) : (
            <p className="m-0 text-[color:var(--color-priority-urgent)]">
              No active recipients — the report is not sent until you add an
              address below.
            </p>
          )
        ) : previewQuery.isError ? (
          <p className="m-0 text-[color:var(--color-ink-subtle)]">
            Recipients unavailable — the preview could not be built.
          </p>
        ) : (
          <p className="m-0 text-[color:var(--color-ink-subtle)]">Loading…</p>
        )}
        {data?.subject ? (
          <p className="mt-1 m-0 text-[color:var(--color-ink-muted)]">
            <span className="font-medium text-[color:var(--color-ink-strong)]">
              Subject:{' '}
            </span>
            {data.subject}
          </p>
        ) : null}
      </div>

      {/* The email itself — sandboxed. */}
      <div className="mt-3">
        {previewQuery.isLoading ? (
          <p className="py-16 text-center text-sm text-[color:var(--color-ink-muted)]">
            Building the preview…
          </p>
        ) : previewQuery.isError ? (
          <p className="py-16 text-center text-sm text-[color:var(--color-priority-urgent)]">
            {extractApiError(previewQuery.error).message}
          </p>
        ) : !data?.html ? (
          <p className="py-16 text-center text-sm italic text-[color:var(--color-ink-muted)]">
            No email template is assigned to the Usage Report yet. Assign one on
            Email Templates → Assignments to preview and send the report.
          </p>
        ) : (
          <>
            {/* The new daily report renders without its template, so it can
                be checked first — but it can't be sent until one is assigned. */}
            {!data.hasTemplate ? (
              <p
                role="alert"
                className="mb-2 rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] px-3 py-2 text-xs text-[color:var(--color-ink)]"
              >
                This report can&rsquo;t be sent yet: assign a template to
                &ldquo;Usage Report (Platform,{' '}
                {PREVIEW_FREQUENCIES.find((f) => f.value === frequency)?.label})&rdquo;
                on Email Templates → Assignments.
              </p>
            ) : null}
            {data.hasErrors ? (
              <p
                role="alert"
                className="mb-2 rounded-lg border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] px-3 py-2 text-xs text-[color:var(--color-danger-text)]"
              >
                Some figures could not be calculated, so a scheduled send right
                now would fail rather than go out incomplete.
                {data.errors?.length ? ` (${data.errors.join('; ')})` : ''}
              </p>
            ) : null}
            <iframe
              title="Usage report email preview"
              sandbox=""
              srcDoc={data.html}
              className="h-[60vh] w-full rounded-lg border border-[color:var(--color-rule)] bg-white"
            />
          </>
        )}
      </div>
      <p className="mt-2 text-[11px] text-[color:var(--color-ink-subtle)]">
        This is a preview only — nothing is sent and no schedule is changed.
      </p>
    </Modal>
  )
}

export function UsageReportPage() {
  const [previewOpen, setPreviewOpen] = useState(false)
  const configQuery = useQuery({
    queryKey: CONFIG_KEY,
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/usage-report')
      return { ...(res.data?.data ?? {}), templatesReady: res.data?.platformTemplatesReady }
    },
  })
  const schedules = configQuery.data?.platformSchedules
  const timezone = configQuery.data?.timezone ?? ''
  const templatesReady = configQuery.data?.templatesReady

  return (
    <div className="pb-16">
      <Breadcrumb current="Usage Report" homePath="/super-admin/dashboard" />

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <FileBarChart
              size={22}
              className="text-[color:var(--color-accent-strong)]"
            />
            Usage Report
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            A periodic summary of platform-wide activity, delivered by email to
            the recipients you configure below.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          onClick={() => setPreviewOpen(true)}
        >
          <Eye size={16} /> Preview report
        </Button>
      </div>

      <DeliveryScheduleSection
        schedules={schedules}
        timezone={timezone}
        loading={configQuery.isLoading}
        templatesReady={templatesReady}
      />

      <ReportRecipientsSection
        queryKey={['platform-usage-report', 'recipients']}
        loadRecipients={async () => {
          const res = await api.get('/super-admin/platform/usage-report')
          return res.data?.data?.recipients ?? []
        }}
        saveRecipients={async (next) => {
          await api.put('/super-admin/platform/usage-report/recipients', {
            recipients: next,
          })
        }}
        description="People who receive the platform-wide Usage Report — a summary covering active companies, new companies, active users, tasks created, AI usage and emails sent for the period. Recipients get the report by email only — no access to the software."
        emptyBody="Add a recipient to receive the platform-wide Usage Report."
      />

      {/* Keyed on open: each opening starts fresh, so the Daily tab always
          starts on the version the Daily schedule actually sends. */}
      <PreviewDialog
        key={previewOpen ? 'open' : 'closed'}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        engines={{
          daily: schedules?.daily?.engine,
          weekly: schedules?.weekly?.engine,
          monthly: schedules?.monthly?.engine,
        }}
      />
    </div>
  )
}
